import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Cookie, X, Settings, Shield, Globe } from "lucide-react";
import { Link } from "react-router-dom";
import { SUPPORTED_LANGUAGES, getSectionTranslations } from "@/i18n";

const COOKIE_CONSENT_KEY = "eduzy_cookie_consent";
const COOKIE_PREFERENCES_KEY = "eduzy_cookie_preferences";
const COOKIE_LANGUAGE_KEY = "eduzy_consent_language";

export function CookieConsent() {
  const [showBanner, setShowBanner] = useState(false);
  const [showPreferences, setShowPreferences] = useState(false);
  const [showLanguageSelector, setShowLanguageSelector] = useState(false);
  const [language, setLanguage] = useState('en');
  const [preferences, setPreferences] = useState({
    necessary: true,
    analytics: false,
    marketing: false,
  });

  // Get translations for current language
  const t = getSectionTranslations(language, 'cookie');
  const commonT = getSectionTranslations(language, 'common');

  useEffect(() => {
    // Load saved language preference
    const savedLang = localStorage.getItem(COOKIE_LANGUAGE_KEY);
    if (savedLang && SUPPORTED_LANGUAGES.find(l => l.code === savedLang)) {
      setLanguage(savedLang);
    }
    
    // Check if user has already made a choice
    const consent = localStorage.getItem(COOKIE_CONSENT_KEY);
    if (!consent) {
      const timer = setTimeout(() => setShowBanner(true), 1500);
      return () => clearTimeout(timer);
    } else {
      const savedPrefs = localStorage.getItem(COOKIE_PREFERENCES_KEY);
      if (savedPrefs) {
        setPreferences(JSON.parse(savedPrefs));
      }
    }
  }, []);

  const handleLanguageChange = (langCode) => {
    setLanguage(langCode);
    localStorage.setItem(COOKIE_LANGUAGE_KEY, langCode);
    setShowLanguageSelector(false);
  };

  const handleAcceptAll = () => {
    const allAccepted = {
      necessary: true,
      analytics: true,
      marketing: true,
    };
    saveConsent(allAccepted);
  };

  const handleAcceptNecessary = () => {
    const necessaryOnly = {
      necessary: true,
      analytics: false,
      marketing: false,
    };
    saveConsent(necessaryOnly);
  };

  const handleSavePreferences = () => {
    saveConsent(preferences);
    setShowPreferences(false);
  };

  const saveConsent = (prefs) => {
    localStorage.setItem(COOKIE_CONSENT_KEY, "true");
    localStorage.setItem(COOKIE_PREFERENCES_KEY, JSON.stringify(prefs));
    setPreferences(prefs);
    setShowBanner(false);
    
    if (prefs.analytics) {
      console.log("Analytics cookies accepted");
    }
  };

  if (!showBanner) return null;

  const currentLang = SUPPORTED_LANGUAGES.find(l => l.code === language);

  return (
    <>
      {/* Main Banner */}
      <div 
        className="fixed bottom-0 left-0 right-0 z-50 p-4 animate-in slide-in-from-bottom duration-300"
        role="dialog"
        aria-labelledby="cookie-banner-title"
        aria-describedby="cookie-banner-description"
      >
        <div className="max-w-4xl mx-auto bg-white rounded-xl shadow-2xl border border-gray-200 overflow-visible">
          {!showPreferences ? (
            // Simple Banner View
            <div className="p-4 md:p-6">
              {/* Language Selector */}
              <div className="flex justify-end items-center gap-2 mb-3">
                <span className="text-xs text-gray-500">{commonT.chooseLanguage || "Choose your language"}</span>
                <div className="relative">
                  <button
                    onClick={() => setShowLanguageSelector(!showLanguageSelector)}
                    className="flex items-center gap-2 px-3 py-1.5 text-sm text-gray-600 hover:text-gray-900 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
                    data-testid="cookie-language-selector"
                  >
                    <Globe className="w-4 h-4" />
                    <span>{currentLang?.nativeName}</span>
                  </button>
                  
                  {showLanguageSelector && (
                    <div className="absolute right-0 bottom-full mb-1 bg-white border border-gray-200 rounded-lg shadow-lg py-1 min-w-[140px] max-h-[280px] overflow-y-auto z-50">
                      {SUPPORTED_LANGUAGES.map((lang) => (
                        <button
                          key={lang.code}
                          onClick={() => handleLanguageChange(lang.code)}
                          className={`w-full px-4 py-2 text-left text-sm hover:bg-gray-100 flex items-center justify-between ${
                            language === lang.code ? 'bg-emerald-50 text-emerald-700' : 'text-gray-700'
                          }`}
                          data-testid={`cookie-lang-${lang.code}`}
                        >
                          <span>{lang.nativeName}</span>
                          {language === lang.code && (
                            <span className="text-emerald-600">✓</span>
                          )}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="w-10 h-10 bg-emerald-100 rounded-lg flex items-center justify-center flex-shrink-0">
                  <Cookie className="w-5 h-5 text-emerald-600" />
                </div>
                <div className="flex-1">
                  <h3 id="cookie-banner-title" className="font-semibold text-gray-900 mb-1">
                    {t.title}
                  </h3>
                  <p id="cookie-banner-description" className="text-sm text-gray-600 mb-4">
                    {t.description}{" "}
                    <Link to="/privacy" className="text-emerald-600 hover:underline">
                      {t.readPrivacyPolicy}
                    </Link>
                  </p>
                  <div className="flex flex-wrap gap-3">
                    <Button 
                      onClick={handleAcceptAll}
                      className="bg-emerald-600 hover:bg-emerald-700"
                      data-testid="cookie-accept-all"
                    >
                      {t.acceptAll}
                    </Button>
                    <Button 
                      variant="outline" 
                      onClick={handleAcceptNecessary}
                      data-testid="cookie-accept-necessary"
                    >
                      {t.necessaryOnly}
                    </Button>
                    <Button 
                      variant="ghost" 
                      onClick={() => setShowPreferences(true)}
                      className="text-gray-600"
                      data-testid="cookie-manage-preferences"
                    >
                      <Settings className="w-4 h-4 mr-2" />
                      {t.managePreferences}
                    </Button>
                  </div>
                </div>
                <button 
                  onClick={handleAcceptNecessary}
                  className="text-gray-400 hover:text-gray-600 p-1"
                  aria-label="Close cookie banner"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
            </div>
          ) : (
            // Preferences View
            <div className="p-4 md:p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-semibold text-gray-900 flex items-center gap-2">
                  <Shield className="w-5 h-5 text-emerald-600" />
                  {t.preferencesTitle}
                </h3>
                <button 
                  onClick={() => setShowPreferences(false)}
                  className="text-gray-400 hover:text-gray-600 p-1"
                  aria-label="Back to banner"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
              
              <div className="space-y-4 mb-6">
                {/* Necessary Cookies */}
                <div className="flex items-start justify-between p-3 bg-gray-50 rounded-lg">
                  <div className="flex-1">
                    <p className="font-medium text-gray-900">{t.necessaryCookies}</p>
                    <p className="text-sm text-gray-500">{t.necessaryDescription}</p>
                  </div>
                  <div className="ml-4">
                    <div className="w-12 h-6 bg-emerald-500 rounded-full flex items-center justify-end px-1">
                      <div className="w-4 h-4 bg-white rounded-full"></div>
                    </div>
                  </div>
                </div>

                {/* Analytics Cookies */}
                <div className="flex items-start justify-between p-3 bg-gray-50 rounded-lg">
                  <div className="flex-1">
                    <p className="font-medium text-gray-900">{t.analyticsCookies}</p>
                    <p className="text-sm text-gray-500">{t.analyticsDescription}</p>
                  </div>
                  <div className="ml-4">
                    <button
                      onClick={() => setPreferences(p => ({ ...p, analytics: !p.analytics }))}
                      className={`w-12 h-6 rounded-full flex items-center px-1 transition-colors ${
                        preferences.analytics ? "bg-emerald-500 justify-end" : "bg-gray-300 justify-start"
                      }`}
                      role="switch"
                      aria-checked={preferences.analytics}
                      data-testid="cookie-toggle-analytics"
                    >
                      <div className="w-4 h-4 bg-white rounded-full shadow"></div>
                    </button>
                  </div>
                </div>

                {/* Marketing Cookies */}
                <div className="flex items-start justify-between p-3 bg-gray-50 rounded-lg">
                  <div className="flex-1">
                    <p className="font-medium text-gray-900">{t.marketingCookies}</p>
                    <p className="text-sm text-gray-500">{t.marketingDescription}</p>
                  </div>
                  <div className="ml-4">
                    <button
                      onClick={() => setPreferences(p => ({ ...p, marketing: !p.marketing }))}
                      className={`w-12 h-6 rounded-full flex items-center px-1 transition-colors ${
                        preferences.marketing ? "bg-emerald-500 justify-end" : "bg-gray-300 justify-start"
                      }`}
                      role="switch"
                      aria-checked={preferences.marketing}
                      data-testid="cookie-toggle-marketing"
                    >
                      <div className="w-4 h-4 bg-white rounded-full shadow"></div>
                    </button>
                  </div>
                </div>
              </div>

              <div className="flex flex-wrap gap-3">
                <Button 
                  onClick={handleSavePreferences}
                  className="bg-emerald-600 hover:bg-emerald-700"
                  data-testid="cookie-save-preferences"
                >
                  {t.savePreferences}
                </Button>
                <Button 
                  variant="outline" 
                  onClick={handleAcceptAll}
                >
                  {t.acceptAll}
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  );
}

// Hook to check cookie preferences
export function useCookiePreferences() {
  const [preferences, setPreferences] = useState({
    necessary: true,
    analytics: false,
    marketing: false,
  });

  useEffect(() => {
    const savedPrefs = localStorage.getItem(COOKIE_PREFERENCES_KEY);
    if (savedPrefs) {
      setPreferences(JSON.parse(savedPrefs));
    }
  }, []);

  return preferences;
}

// Hook to get saved consent language
export function useConsentLanguage() {
  const [language, setLanguage] = useState('en');
  
  useEffect(() => {
    const savedLang = localStorage.getItem(COOKIE_LANGUAGE_KEY);
    if (savedLang) {
      setLanguage(savedLang);
    }
  }, []);
  
  return language;
}

export default CookieConsent;
